import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Menu,
  X,
  ChevronDown,
  Heart,
  BookOpen,
  HelpCircle,
  Phone,
  DollarSign,
  Package,
  User,
  LogIn,
  Calculator,
  Smartphone,
  Home,
  FileCode,
} from "lucide-react";
import Images from "../../utils/Images";

const TopNavbar = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileResourcesOpen, setMobileResourcesOpen] = useState(false);
  const resourcesRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };

    const handleClickOutside = (event) => {
      if (
        resourcesRef.current &&
        !resourcesRef.current.contains(event.target)
      ) {
        setResourcesOpen(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleSidebar = () => {
    setIsOpen(!isOpen);
    if (!isOpen === false) {
      setMobileResourcesOpen(false);
      setResourcesOpen(false);
    }
  };

  const closeSidebar = () => {
    setIsOpen(false);
    setMobileResourcesOpen(false);
  };

  // const toggleResources = () => {
  //   setResourcesOpen(!resourcesOpen);
  // };

  const toggleMobileResources = () => {
    setMobileResourcesOpen(!mobileResourcesOpen);
  };

  const handleSmoothScroll = (e, targetId) => {
    e.preventDefault();
    const targetElement = document.getElementById(targetId);
    if (targetElement) {
      targetElement.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
    if (isOpen) {
      closeSidebar();
    }
  };

  const navItems = [
    {
      name: "Home",
      path: "/",
      icon: <Home className="w-5 h-5 text-red-600" />,
    },
    {
      name: "Products",
      path: "/products",
      icon: <Package className="w-5 h-5 text-red-600" />,
    },
    // {
    //   name: "Resources",
    //   path: "#",
    //   icon: <BookOpen className="w-5 h-5 text-red-600" />,
    //   hasMegaMenu: true,
    //   action: toggleResources,
    // },
    ...(location.pathname === "/" || location.pathname === "/home"
      ? [
          {
            name: "Pricing",
            path: "#pricing",
            icon: <DollarSign className="w-5 h-5 text-red-600" />,
          },
        ]
      : []),
    {
      name: "API Docs",
      path: "/ApiDocumentation",
      icon: <FileCode className="w-5 h-5 text-red-600" />,
    },
    {
      name: "Contact Us",
      path: "/contact-us",
      icon: <Phone className="w-5 h-5 text-red-600" />,
    },
    // {
    //   name: "About Us",
    //   path: "/about",
    //   icon: <Info className="w-5 h-5 text-red-600" />,
    // },
  ];

  const resourceCategories = [
    {
      title: "TEMPLATES",
      items: [
        {
          icon: <Heart className="w-5 h-5 text-red-600" />,
          title: "SMS We Love",
          description: "Real SMS examples to inspire your next campaign",
          path: "/resources/sms-examples",
          tag: "CASE STUDY",
        },
        {
          icon: <Smartphone className="w-5 h-5 text-red-600" />,
          title: "Case Studies",
          description:
            "Explore proven strategies and results from innovative brands",
          path: "/resources/case-studies",
          tag: "",
        },
      ],
    },
    {
      title: "HELP",
      items: [
        {
          icon: <BookOpen className="w-5 h-5 text-red-600" />,
          title: "Guides",
          description:
            "Text Messaging industry insights to help you become an SMS expert",
          path: "/resources/guides",
          tag: "",
        },
        {
          icon: <HelpCircle className="w-5 h-5 text-red-600" />,
          title: "Help Center",
          description: "Check out our how to guides, webinar, and more",
          path: "/resources/help-center",
          tag: "",
        },
      ],
    },
    {
      title: "TOOL",
      items: [
        {
          icon: <Calculator className="w-5 h-5 text-red-600" />,
          title: "Revenue Calculator",
          description:
            "Instantly calculate your projected revenue and subscriber growth",
          path: "/resources/revenue-calculator",
          tag: "",
        },
      ],
    },
  ];

  const megaMenuVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        staggerChildren: 0.04,
      },
    },
    exit: {
      opacity: 0,
      y: -10,
      transition: {
        duration: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={closeSidebar}
        />
      )}

      {/* Navbar */}
      <nav
        className={`fixed top-0 left-0 right-0 z-30 transition-all duration-300 overflow-hidden ${
          scrolled ? "bg-white shadow-md" : "bg-white"
        }`}
      >
        <div className="overflow-hidden px-4 mx-auto max-w-8xl sm:px-6 lg:px-8">
          <div className="flex overflow-hidden justify-between items-center h-16">
            <div className="flex flex-shrink-0 items-center">
              <Link to="/" className="flex items-center">
                <img src={Images.logo} alt="logo" className="h-8" />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex lg:items-center lg:space-x-8">
              {navItems.map((item) =>
                item.hasMegaMenu ? (
                  <div key={item.name} className="relative" ref={resourcesRef}>
                    <button
                      onClick={item.action}
                      className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:text-red-600"
                    >
                      {item.name}
                      <ChevronDown
                        className={`ml-1 w-4 h-4 text-red-600 transform transition-transform duration-200 ${
                          resourcesOpen ? "rotate-180" : ""
                        }`}
                      />
                    </button>

                    <AnimatePresence>
                      {resourcesOpen && (
                        <motion.div
                          initial="hidden"
                          animate="visible"
                          exit="exit"
                          variants={megaMenuVariants}
                          className="grid absolute left-0 top-full z-50 grid-cols-3 gap-6 p-6 mt-1 w-screen max-w-6xl bg-white rounded-lg shadow-xl"
                          style={{ left: "-250px" }}
                        >
                          {resourceCategories.map((category, index) => (
                            <motion.div
                              key={category.title}
                              variants={itemVariants}
                              className={`space-y-6 ${
                                index === 2 ? "col-span-1 pr-0" : "col-span-1"
                              }`}
                            >
                              <div className="px-3 py-1.5 rounded-full bg-red-100 text-red-700 text-xs font-medium inline-block">
                                {category.title}
                              </div>
                              <div className="space-y-6">
                                {category.items.map((item) => (
                                  <motion.div
                                    key={item.title}
                                    variants={itemVariants}
                                  >
                                    <Link
                                      to={item.path}
                                      className="flex flex-col p-3 space-y-2 rounded-lg transition-colors duration-200 group hover:bg-gray-50"
                                      onClick={() => setResourcesOpen(false)}
                                    >
                                      <div className="flex items-center space-x-3">
                                        {item.icon}
                                        <span className="font-medium text-gray-800 transition-colors duration-200 group-hover:text-red-600">
                                          {item.title}
                                        </span>
                                        {item.tag && (
                                          <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                                            {item.tag}
                                          </span>
                                        )}
                                      </div>
                                      <p className="pl-8 text-sm text-gray-500">
                                        {item.description}
                                      </p>
                                    </Link>
                                  </motion.div>
                                ))}
                              </div>
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : item.path.startsWith("#") ? (
                  <button
                    key={item.name}
                    onClick={(e) =>
                      handleSmoothScroll(e, item.path.substring(1))
                    }
                    className="px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:text-red-600"
                  >
                    {item.name}
                  </button>
                ) : (
                  <Link
                    key={item.name}
                    to={item.path}
                    className="px-3 py-2 text-sm font-medium text-gray-700 transition-colors duration-200 hover:text-red-600"
                  >
                    {item.name}
                  </Link>
                )
              )}
            </div>

            {/* Authentication Buttons - Desktop */}
            <div className="hidden lg:flex lg:items-center lg:space-x-4">
              <Link
                to="/login"
                className="px-3 py-2 text-sm font-medium text-gray-700 hover:text-red-600"
              >
                Login
              </Link>
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="/signup"
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md transition-colors duration-200 hover:bg-red-700"
                >
                  Sign Up
                </Link>
              </motion.div>
            </div>

            {/* Mobile menu button */}
            <div className="flex lg:hidden">
              <button
                onClick={toggleSidebar}
                className="inline-flex justify-center items-center p-2 text-gray-700 rounded-md hover:text-red-600 focus:outline-none"
              >
                <span className="sr-only">Open main menu</span>
                <Menu className="block w-6 h-6 text-red-600" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-0 right-0 z-50 w-72 h-full bg-white shadow-lg lg:hidden"
          >
            <div className="flex justify-between items-center p-4 border-b">
              <div className="flex items-center">
                <img src={Images.logo} alt="logo" className="h-8" />
              </div>
              <motion.button
                onClick={closeSidebar}
                className="text-gray-700 hover:text-red-600 focus:outline-none"
                whileHover={{ rotate: 90 }}
                transition={{ duration: 0.2 }}
              >
                <X className="w-6 h-6 text-red-600" />
              </motion.button>
            </div>

            {/* Mobile Content */}
            <div className="py-4 overflow-y-auto max-h-[calc(100vh-80px)]">
              {/* Mobile Navigation Links */}
              <div className="px-2 space-y-1">
                {navItems.map((item) => (
                  <motion.div
                    key={item.name}
                    whileHover={{ x: 5 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    {item.hasMegaMenu ? (
                      <div className="space-y-3">
                        <button
                          onClick={toggleMobileResources}
                          className="flex justify-between items-center px-3 py-2 w-full text-base font-medium text-gray-700 rounded-md transition-colors duration-200 hover:bg-gray-100 hover:text-red-600"
                        >
                          <div className="flex items-center">
                            {item.icon}
                            <span className="ml-2">{item.name}</span>
                          </div>
                          <ChevronDown
                            className={`w-4 h-4 text-red-600 transform transition-transform duration-200 ${
                              mobileResourcesOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        <AnimatePresence>
                          {mobileResourcesOpen && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: "auto", opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="overflow-hidden pl-5"
                            >
                              {resourceCategories.map((category) => (
                                <div key={category.title} className="mb-4">
                                  <div className="px-3 mb-2 text-xs font-semibold text-red-700">
                                    {category.title}
                                  </div>

                                  {category.items.map((subItem) => (
                                    <Link
                                      key={subItem.title}
                                      to={subItem.path}
                                      className="flex items-center px-3 py-2 text-sm text-gray-700 rounded-md transition-colors duration-200 hover:bg-gray-100 hover:text-red-600"
                                      onClick={closeSidebar}
                                    >
                                      {subItem.icon}
                                      <span className="ml-2">
                                        {subItem.title}
                                      </span>
                                    </Link>
                                  ))}
                                </div>
                              ))}
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    ) : item.path.startsWith("#") ? (
                      <button
                        onClick={(e) =>
                          handleSmoothScroll(e, item.path.substring(1))
                        }
                        className="flex items-center px-3 py-2 w-full text-base font-medium text-gray-700 rounded-md transition-colors duration-200 hover:bg-gray-100 hover:text-red-600"
                      >
                        {item.icon}
                        <span className="ml-2">{item.name}</span>
                      </button>
                    ) : (
                      <Link
                        to={item.path}
                        className="flex items-center px-3 py-2 text-base font-medium text-gray-700 rounded-md transition-colors duration-200 hover:bg-gray-100 hover:text-red-600"
                        onClick={closeSidebar}
                      >
                        {item.icon}
                        <span className="ml-2">{item.name}</span>
                      </Link>
                    )}
                  </motion.div>
                ))}
              </div>

              {/* Divider */}
              <div className="my-4 border-t border-gray-200"></div>

              {/* Authentication Links - Mobile */}
              <div className="px-2 space-y-3">
                <motion.div whileHover={{ x: 5 }} whileTap={{ scale: 0.97 }}>
                  <Link
                    to="/login"
                    className="flex items-center px-3 py-2 text-base font-medium text-gray-700 rounded-md transition-colors duration-200 hover:bg-gray-100 hover:text-red-600"
                    onClick={closeSidebar}
                  >
                    <LogIn className="mr-2 w-5 h-5 text-red-600" />
                    Login
                  </Link>
                </motion.div>
                <motion.div whileHover={{ x: 5 }} whileTap={{ scale: 0.97 }}>
                  <Link
                    to="/signup"
                    className="flex items-center px-3 py-2 text-base font-medium text-white bg-red-600 rounded-md transition-colors duration-200 hover:bg-red-700"
                    onClick={closeSidebar}
                  >
                    <User className="mr-2 w-5 h-5 text-white" />
                    Sign Up
                  </Link>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default TopNavbar;
